import type { PageSeoMeta } from "./types";
import { ALL_FORMAT_SLUG_EXAMPLES } from "@textformatter/formatter";
import { BLOG_POSTS } from "../data/blogPosts";

export type SeoMeta = PageSeoMeta;

export const DEFAULT_DESCRIPTION =
  "Format messy text into clean, structured output for web, ecommerce, or plain text in seconds.";

export function cleanPath(path: string) {
  if (!path) return "/";
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return normalized.length > 1 ? normalized.replace(/\/+$/, "") : normalized;
}

export function getSiteUrl(baseUrl?: string) {
  const fallback = "https://www.textformatter.pro";
  const candidate = (baseUrl || "").trim();
  if (!candidate) return fallback;
  return candidate.replace(/\/+$/, "");
}

export function buildAbsoluteUrl(siteUrl: string, path: string) {
  const base = getSiteUrl(siteUrl);
  const normalizedPath = cleanPath(path);
  return normalizedPath === "/" ? `${base}/` : `${base}${normalizedPath}`;
}

export function getHomepageJsonLd(siteUrl: string) {
	return [
		{
			"@context": "https://schema.org",
			"@type": "WebSite",
			name: "Text Formatter",
			alternateName: "TextFormatter",
			url: `${siteUrl}/`,
		},
		{
			"@context": "https://schema.org",
			"@type": "SoftwareApplication",
			name: "Text Formatter",
			applicationCategory: "BusinessApplication",
			operatingSystem: "Web",
			description: DEFAULT_DESCRIPTION,
			url: `${siteUrl}/`,
		},
		{
			"@context": "https://schema.org",
			"@type": "WebApplication",
			name: "Text Formatter",
			applicationCategory: "BusinessApplication",
			operatingSystem: "Web",
			description: DEFAULT_DESCRIPTION,
			url: `${siteUrl}/`,
		},
		{
			"@context": "https://schema.org",
			"@type": "WebAPI",
			name: "Text Formatter API",
			description: "API for formatting raw text into clean, structured text or HTML output.",
			documentation: "https://api.textformatter.pro",
			provider: {
				"@type": "Organization",
				name: "Text Formatter",
				url: `${siteUrl}/`,
			},
			url: "https://api.textformatter.pro/v1/format",
		},
	];
}

export function getFaqJsonLd(siteUrl: string) {
	return {
		"@context": "https://schema.org",
		"@type": "FAQPage",
		mainEntity: [
			{
				"@type": "Question",
				name: "What does Text Formatter do?",
				acceptedAnswer: {
					"@type": "Answer",
					text: "It cleans messy text, improves spacing, detects structure, and outputs either plain text or HTML.",
				},
			},
			{
				"@type": "Question",
				name: "Can I use it for free?",
				acceptedAnswer: {
					"@type": "Answer",
					text: "Yes. The web app is free. If you need API access, you get a free API key with a generous word limit.",
				},
			},
			{
				"@type": "Question",
				name: "Can I integrate it into my app?",
				acceptedAnswer: {
					"@type": "Answer",
					text: "Yes. Use the /v1/format API endpoint with your API key to automate formatting in your workflows.",
				},
			},
		],
		mainEntityOfPage: `${siteUrl}/faq`,
	};
}

export function getBlogIndexJsonLd(siteUrl: string) {
	return {
		"@context": "https://schema.org",
		"@type": "Blog",
		name: "Text Formatter Blog",
		url: `${siteUrl}/blog`,
		description:
			"Product updates, practical workflows, and content quality guides from the Text Formatter team.",
		blogPost: BLOG_POSTS.map((post) => ({
			"@type": "BlogPosting",
			headline: post.title,
			url: `${siteUrl}/blog/${post.slug}`,
			datePublished: post.publishedAt,
			description: post.excerpt,
			author: {
				"@type": "Organization",
				name: "Text Formatter",
			},
			publisher: {
				"@type": "Organization",
				name: "Text Formatter",
			},
		})),
	};
}

export function getBlogPostJsonLd(
	siteUrl: string,
	post: { slug: string; title: string; publishedAt: string; excerpt: string },
) {
	return {
		"@context": "https://schema.org",
		"@type": "BlogPosting",
		headline: post.title,
		description: post.excerpt,
		url: `${siteUrl}/blog/${post.slug}`,
		mainEntityOfPage: `${siteUrl}/blog/${post.slug}`,
		datePublished: post.publishedAt,
		author: {
			"@type": "Organization",
			name: "Text Formatter",
		},
		publisher: {
			"@type": "Organization",
			name: "Text Formatter",
		},
	};
}

export function getFormatPageJsonLd(
  siteUrl: string,
  pathname: string,
  title: string,
  description: string,
) {
  const url = `${siteUrl}${pathname}`;
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: title,
    description,
    url,
    isPartOf: {
      "@type": "WebSite",
      name: "Text Formatter",
      url: `${siteUrl}/`,
    },
  };
}

export function getIndexableRoutes() {
  const formatRoutes = ALL_FORMAT_SLUG_EXAMPLES.map((example) => `/format/${example.slug}`);
  const blogRoutes = BLOG_POSTS.map((post) => `/blog/${post.slug}`);
  return [
    "/",
    "/examples",
    "/faq",
    "/pricing",
    "/status",
    "/blog",
    "/about",
    "/contact",
    "/terms",
    "/privacy",
    ...formatRoutes,
    ...blogRoutes,
  ];
}
